import { Platform } from 'react-native';

import { safeTabBlobUrl, safeTabType, saveResponseAs } from '@/utils/safe-blob-types';

/** Base64 without a dependency: `btoa` exists on web and Hermes, but only for binary strings. */
function bytesToBase64(bytes: Uint8Array): string {
	let binary = '';
	// Chunked: String.fromCharCode over a whole PDF overflows the argument limit.
	for (let i = 0; i < bytes.length; i += 0x8000) {
		binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
	}
	return btoa(binary);
}

/**
 * Show a PDF the app had to fetch itself — one behind auth, so a plain link cannot open it.
 *
 * WEB: the tab is opened BEFORE the first `await`, while the click still counts as a user
 * gesture. Opening it after the fetch is what popup blockers refuse, and then "Open" silently
 * does nothing. The blank tab is pointed at the blob once it arrives, and closed on failure.
 *
 * NATIVE (iOS): there is no `window` and no blob URL. The bytes go to a cache file and the
 * share sheet, whose Quick Look preview shows the PDF — the pattern `utils/accounting-export.ts`
 * uses for exports.
 *
 * Call it synchronously from the press handler (no `await` before it), or the web tab
 * reservation happens outside the gesture and the point is lost.
 */
export async function openFetchedPdf(fetchPdf: () => Promise<Response>, fileName: string): Promise<void> {
	return openFetchedFile(fetchPdf, fileName, 'application/pdf');
}

/**
 * {@link openFetchedPdf} for any file type (#603's reply attachments).
 *
 * The type matters on iOS, where it decides which apps the share sheet offers: a photo or a
 * Word document sent as `application/pdf` is offered to PDF readers and fails to preview. The
 * PDF UTI is passed only for a PDF; for anything else iOS infers the type from the extension.
 */
export async function openFetchedFile(
	fetchPdf: () => Promise<Response>, fileName: string, mimeType: string,
): Promise<void> {
	const isPdf = mimeType === 'application/pdf';
	if (Platform.OS === 'web') {
		/**
		 * A blob URL runs in OUR origin, so only `safeTabType` files are rendered, re-typed from
		 * the extension. Anything else — an emailed .html or .svg — is saved, never rendered.
		 * Decided from the name before any await, so no tab is reserved for a download.
		 */
		if (!safeTabType(fileName)) {
			const response = await fetchPdf();
			if (!response.ok) throw new Error(`Could not open the file (${response.status})`);
			await saveResponseAs(response, fileName);
			return;
		}
		const tab = typeof window !== 'undefined' ? window.open('', '_blank') : null;
		try {
			const response = await fetchPdf();
			if (!response.ok) throw new Error(`Could not open the file (${response.status})`);
			const url = await safeTabBlobUrl(response, fileName);
			if (!url) throw new Error('This file type cannot be previewed');
			// Not revoked: the tab reads it after this returns.
			if (tab) {
				tab.opener = null;
				tab.location.href = url;
			} else {
				window.open(url, '_blank', 'noopener,noreferrer');
			}
		} catch (e) {
			tab?.close();
			throw e;
		}
		return;
	}

	/**
	 * Loaded here, not at the top: this module is imported by `data-service`, which half the
	 * app (and every jsdom spec) loads, and the native modules' eager init crashes off-device.
	 * Only this branch — native, after a tap — needs them.
	 */
	const [FileSystem, Sharing] = await Promise.all([import('expo-file-system/legacy'), import('expo-sharing')]);
	const response = await fetchPdf();
	if (!response.ok) throw new Error(`Could not open the file (${response.status})`);
	const safeName = fileName.replace(/[\\/:*?"<>|]+/g, '-');
	const uri = `${FileSystem.cacheDirectory}${safeName}`;
	await FileSystem.writeAsStringAsync(uri, bytesToBase64(new Uint8Array(await response.arrayBuffer())), {
		encoding: FileSystem.EncodingType.Base64,
	});
	if (!(await Sharing.isAvailableAsync())) throw new Error('Opening files is not available on this device');
	await Sharing.shareAsync(uri, { mimeType, ...(isPdf ? { UTI: 'com.adobe.pdf' } : {}), dialogTitle: fileName });
}
